import React, { useCallback, useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import allyiqLogo from "../../assets/allyiq-logo.svg";
import allyiqIcon from "../../assets/allyiq-icon.svg";
import "../../styles/components/Sidebar.scss";
import Icon from "./Icon";
import { ScrollPanel } from "primereact/scrollpanel";
import { Tooltip } from "primereact/tooltip";
import { useAppContext } from "../../contexts/AppContext";
import { AppModule } from "@/enums/AppModule";
import useConfig from "@/hooks/useConfig";

interface SidebarProps {
  isSidebarCollapsed: boolean;
  setIsSidebarCollapsed: React.Dispatch<React.SetStateAction<boolean>>;
}

const Sidebar: React.FC<SidebarProps> = ({
  isSidebarCollapsed,
  setIsSidebarCollapsed,
}) => {
  const { availableModules, userContextData } = useAppContext();
  const { data: configData } = useConfig();
  const isProdEnvironment = configData?.environment === "prod";
  const isPreprodEnvironment = configData?.environment === "preprod";
  const configVersion =
    typeof configData?.version === "string" ? configData.version.trim() : "";
  const appVersion =
    configVersion && !configVersion.includes("#{") ? configVersion : "Develop";
  const displayVersion = `Version: ${appVersion}`;
  interface ISidebarLink {
    linkTo: string;
    label: string;
    iconName: string;
    isHidden: boolean;
    order: number;
  }

  const [menuItems, setMenuItems] = useState<ISidebarLink[]>([]);

  const hasModuleAccess = useCallback(
    (module: AppModule) => availableModules.includes(module),
    [availableModules],
  );

  const sortMenuItems = useCallback((menus: ISidebarLink[]) => {
    return menus
      .filter((menu) => !menu.isHidden)
      .sort((a, b) => a.order - b.order);
  }, []);

  const loadMenuItems = useCallback(() => {
    const menus: ISidebarLink[] = [
      // {
      //   linkTo: "/dashboard",
      //   label: "Dashboard",
      //   iconName: "pi-home",
      //   isHidden: false,
      //   order: 1,
      // },
      {
        linkTo: "/demand-forecast",
        label: "Demand Forecast",
        iconName: "pi-chart-bar",
        isHidden: !hasModuleAccess(AppModule.DemandForecast),
        order: 2,
      },
      {
        linkTo: "/forecasts",
        label: "Usage Forecast",
        iconName: "pi-chart-line",
        isHidden:
          !hasModuleAccess(AppModule.UsageForecast) || isProdEnvironment,
        order: 3,
      },
      {
        linkTo: "/retina-forecast",
        label: "Retina Forecast",
        iconName: "pi-chart-line",
        isHidden: !hasModuleAccess(AppModule.RetinaForecast),
        order: 4,
      },
      {
        linkTo: "/allocation-check",
        label: "Allocation Check",
        iconName: "pi-check-circle",
        isHidden: !hasModuleAccess(AppModule.AllocationCheckList),
        order: 5,
      },
      {
        linkTo: "/report-comparator",
        label: "Report Comparator",
        iconName: "pi-file-excel",
        isHidden: !userContextData?.IsInternalRole || !isPreprodEnvironment,
        order: 6,
      },
      {
        linkTo: "/settings",
        label: "Settings",
        iconName: "pi-cog",
        isHidden: availableModules.length === 0,
        order: 7,
      },
    ];

    setMenuItems(sortMenuItems(menus));
  }, [
    sortMenuItems,
    hasModuleAccess,
    availableModules.length,
    isProdEnvironment,
    isPreprodEnvironment,
    userContextData?.IsInternalRole,
  ]);

  useEffect(() => {
    loadMenuItems();
  }, [loadMenuItems]);

  const toggleSidebar = () => {
    setIsSidebarCollapsed(!isSidebarCollapsed);
  };

  return (
    <div
      className={`app-sidebar ${isSidebarCollapsed ? "collapsed-sidebar" : ""}`}
      data-testid="sidebar"
    >
      <button
        className="sidebar-toggle-btn"
        onClick={toggleSidebar}
        aria-label={isSidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
      >
        <i
          className={`pi ${isSidebarCollapsed ? "pi-chevron-right" : "pi-chevron-left"} sidebar-toggle-icon`}
        ></i>
      </button>
      <div className="logo-container">
        <span className="p-image p-component App-logo align-items-center flex justify-content-center">
          <img
            src={isSidebarCollapsed ? allyiqIcon : allyiqLogo}
            width="100%"
            height="100%"
            alt="AllyIQ"
          />
        </span>
      </div>

      <ScrollPanel className="p-scrollpanel">
        <nav>
          <ul>
            {menuItems.map((item, index) => (
              <li key={item.linkTo}>
                <NavLink
                  to={item.linkTo}
                  className={({ isActive }) =>
                    `menu-item align-items-center ${isActive ? "active" : ""}`
                  }
                  data-pr-tooltip={isSidebarCollapsed ? item.label : undefined}
                  data-pr-position="right"
                  data-pr-classname="sidebar-tooltip"
                  id={`menu-item-${index}`}
                >
                  {item.iconName.startsWith("pi-") ? (
                    <i className={`pi ${item.iconName} menu-icon`}></i>
                  ) : (
                    <Icon iconName={item.iconName} />
                  )}
                  <div className="menu-label">{item.label}</div>
                </NavLink>
                {isSidebarCollapsed && (
                  <Tooltip target={`#menu-item-${index}`} />
                )}
              </li>
            ))}
          </ul>
        </nav>
      </ScrollPanel>

      <div className="side-menu-footer">
        <div
          className="version-link"
          title={`Version ${displayVersion}`}
          aria-label={`Application version ${displayVersion}`}
        >
          <span>{displayVersion}</span>
        </div>
      </div>
    </div>
  );
};

export default Sidebar;
